import QueryConstants from './QueryConstants';

import type { Route } from 'nextjs-routes';
import type { PropertiesListViewMode } from './PropertiesListConstants';

export const ROUTES = {
  PROPERTIES: '/properties',
  LISTINGS: '/listings',
  LISTING: '/properties/[listingAlias]/listing',
  CREATE_LISTING: '/properties/[listingAlias]/create-listing',
  METRICS: '/properties/[listingAlias]/metrics',
  LISTING_SOURCES: '/properties/[listingAlias]/sources',
  FEATURE_FLAGS: '/featureFlags',
  SECURITY_INQUIRY: '/security-inquiry',
  SUBMIT_REQUEST: '/submit-request',
  LISTING_CLAIMING: '/listing-claiming',
  DASHBOARD_OVERVIEW: '/properties/[listingAlias]/dashboard/overview',
  DASHBOARD_LISTING: '/properties/[listingAlias]/dashboard/listing',
  DASHBOARD_APPLICATIONS: '/properties/[listingAlias]/dashboard/applications',
  DASHBOARD_LEASES: '/properties/[listingAlias]/dashboard/leases',
  DASHBOARD_PAYMENTS: '/properties/[listingAlias]/dashboard/payments',
  DASHBOARD_MAINTENANCE_TASKS: '/properties/[listingAlias]/dashboard/maintenance-tasks',
  LEAD: '/lead/[leadId]',
  INQUIRY_CONTACT: '/inquiry-contact',
  TOUR_AVAILABILITY: '/properties/[listingAlias]/tours/tour-availability',
  TOUR_CALENDAR: '/properties/[listingAlias]/tours/tour-calendar',
  SELECT_PROPERTY: '/select-property',
  ACCOUNT_SETTINGS: '/account',
  APPLICATION_SETTINGS: '/account/applications',
  RENTAL_PROTECTION_MANAGE_POLICIES: '/rental-protection/properties/[propertyId]/managePolicies/[[...moduleId]]',
  PORTFOLIO_MEMBERS: '/account/portfolios/[portfolioId]/members',
  PORTFOLIO_TEAMS: '/account/portfolios/[portfolioId]/teams',
  PORTFOLIO_PROPERTIES: '/account/portfolios/[portfolioId]/properties',
  PREMIUM_ANALYTICS: '/properties/[listingAlias]/premium-analytics',
  COSTS_AND_FEES: '/properties/[listingAlias]/costs-and-fees',
  PHOTO_UPLOAD: '/properties/[listingAlias]/photo-upload',
  MOBILE_VERIFY_OWNERSHIP: '/properties/[listingAlias]/verify-ownership',
  VERIFY_IDENTITY: '/verify-identity',
} as const;

export const EXTERNAL_ROUTES = {
  SIGN_IN: '/rental-manager/login/',
  PROPERTIES: '/rental-manager/properties',
  LISTINGS: '/rental-manager/listings',
  PAYMENTS_AGGREGATE_PAGE: '/rental-manager/rent-payment/overview',
  PAYMENTS_GET_STARTED: '/rental-manager/rent-payment/get-started',
  RENT_PAYMENT_PAGE: '/rental-manager/rent-payment',
  PAYMENTS_DASHBOARD: '/rental-manager/payments/properties',
  OLYMPUS_REDIRECT: '/rental-manager/ad-management/ad-management-non-advertiser-olympus',
  SETUP_TASKS: '/rental-manager/ad-management/setup-tasks',
  ZRM_HOME_PAGE: '/rental-manager',
  MAINTENANCE_TASKS: '/rental-manager/maintenance-tasks',
  ZILLOW_ACCOUNT_SETTINGS: '/myzillow/account/',
  APPLICATIONS_PROPERTY: '/rental-manager/applications/property',
  LEASING_PROPERTY: '/rental-manager/leasing/property',
  LEAD_MANAGEMENT: '/rental-manager/lead-management',
  CONTACTS: '/rental-manager/leads',
  INBOX: '/rental-manager/inbox',
} as const;

/**
 * Converts a backend-provided CTA URL (relative and prefixed with the `rental-manager`
 * base path) into an in-app Next.js route the router can navigate to.
 */
export function toAppRoute(ctaUrl: string): Route {
  const withoutBasePath = ctaUrl.trim().replace(/^\/?rental-manager\/?/, '/');

  return (withoutBasePath.startsWith('/') ? withoutBasePath : `/${withoutBasePath}`) as unknown as Route;
}

const RETURN_TO_PLACEHOLDER_ORIGIN = 'https://zrm.invalid';

/**
 * Where leaving the verify identity page goes: back to the in-app path it was opened from, or Properties when
 * `returnTo` is missing, could leave the app, or points at the verify page itself.
 */
export function getVerifyIdentityExitRoute(returnTo: unknown): Route {
  const fallback = ROUTES.PROPERTIES as unknown as Route;
  if (typeof returnTo !== 'string' || !returnTo.startsWith('/')) return fallback;

  // Parsed the way a browser will (tabs and newlines stripped, backslashes read as slashes), so anything
  // that resolves to another origin is caught however it's spelled.
  let url: URL;
  try {
    url = new URL(returnTo, RETURN_TO_PLACEHOLDER_ORIGIN);
  } catch {
    return fallback;
  }
  if (url.origin !== RETURN_TO_PLACEHOLDER_ORIGIN || url.pathname === ROUTES.VERIFY_IDENTITY) return fallback;

  return `${url.pathname}${url.search}${url.hash}` as unknown as Route;
}

export function getPropertiesRoute({
  viewMode,
  expandedGroup,
}: {
  viewMode?: PropertiesListViewMode;
  expandedGroup?: string;
} = {}): {
  pathname: Route['pathname'];
  query: Route['query'];
} {
  return {
    pathname: ROUTES.PROPERTIES,
    query: {
      ...(viewMode ? { [QueryConstants.VIEW_MODE]: viewMode } : {}),
      ...(expandedGroup ? { [QueryConstants.EXPANDED_GROUP]: expandedGroup } : {}),
    },
  };
}

export function getListingRoute({ listingAlias }: { listingAlias: string }): {
  pathname: Route['pathname'];
  query: Route['query'];
} {
  return {
    pathname: ROUTES.LISTING,
    query: { listingAlias },
  };
}

export function getCreateListingRoute({
  listingAlias,
  multiplexBuildingSessionId,
  step,
  listingCloneConversionSuccess,
}: {
  listingAlias: string;
  multiplexBuildingSessionId?: string;
  step?: string;
  listingCloneConversionSuccess?: boolean;
}): {
  pathname: Route['pathname'];
  query: Route['query'];
} {
  return {
    pathname: ROUTES.CREATE_LISTING,
    query: {
      listingAlias,
      ...(multiplexBuildingSessionId ? { multiplexBuildingSessionId } : {}),
      ...(step ? { step } : {}),
      ...(listingCloneConversionSuccess ? { listingCloneConversionSuccess: 'true' } : {}),
    },
  };
}

export function getListingSourcesRoute({ listingAlias }: { listingAlias: string }): {
  pathname: Route['pathname'];
  query: Route['query'];
} {
  return {
    pathname: ROUTES.LISTING_SOURCES,
    query: { listingAlias },
  };
}

export function getDashboardOverviewRoute({
  listingAlias,
  showActivationModal,
  showSuccessModal,
}: {
  listingAlias: string;
  showActivationModal?: boolean;
  showSuccessModal?: boolean;
}): {
  pathname: Route['pathname'];
  query: Route['query'];
} {
  return {
    pathname: ROUTES.DASHBOARD_OVERVIEW,
    query: {
      listingAlias,
      ...(showActivationModal ? { showActivationModal: 'true' } : {}),
      ...(showSuccessModal ? { showSuccessModal: 'true' } : {}),
    },
  };
}

export function getPropertyRoute({ listingAlias, isMultifamily }: { listingAlias: string; isMultifamily?: boolean }): {
  pathname: Route['pathname'];
  query: Route['query'];
} {
  return isMultifamily ? getListingRoute({ listingAlias }) : getDashboardOverviewRoute({ listingAlias });
}

export function getPremiumAnalyticsRoute({ listingAlias }: { listingAlias: string }): {
  pathname: Route['pathname'];
  query: Route['query'];
} {
  return {
    pathname: ROUTES.PREMIUM_ANALYTICS,
    query: { listingAlias },
  };
}

export function getDashboardListingRoute({ listingAlias }: { listingAlias: string }): {
  pathname: Route['pathname'];
  query: Route['query'];
} {
  return {
    pathname: ROUTES.DASHBOARD_LISTING,
    query: { listingAlias },
  };
}

export function getDashboardApplicationsRoute({ listingAlias }: { listingAlias: string }): {
  pathname: Route['pathname'];
  query: Route['query'];
} {
  return {
    pathname: ROUTES.DASHBOARD_APPLICATIONS,
    query: { listingAlias },
  };
}

export function getApplicationsPropertyRoute({ listingAlias }: { listingAlias: string }): string {
  return `${EXTERNAL_ROUTES.APPLICATIONS_PROPERTY}/${listingAlias}`;
}

export function getLeasingPropertyRoute({ listingAlias }: { listingAlias: string }): string {
  return `${EXTERNAL_ROUTES.LEASING_PROPERTY}/${listingAlias}`;
}

export function getPaymentsDashboardRoute({ listingAlias }: { listingAlias: string }): string {
  return `${EXTERNAL_ROUTES.PAYMENTS_DASHBOARD}/${listingAlias}`;
}

export function getDashboardLeasesRoute({ listingAlias }: { listingAlias: string }): {
  pathname: Route['pathname'];
  query: Route['query'];
} {
  return {
    pathname: ROUTES.DASHBOARD_LEASES,
    query: { listingAlias },
  };
}

export function getDashboardPaymentsRoute({ listingAlias }: { listingAlias: string }): {
  pathname: Route['pathname'];
  query: Route['query'];
} {
  return {
    pathname: ROUTES.DASHBOARD_PAYMENTS,
    query: { listingAlias },
  };
}

export function getDashboardMaintenanceTasksRoute({ listingAlias }: { listingAlias: string }): {
  pathname: Route['pathname'];
  query: Route['query'];
} {
  return {
    pathname: ROUTES.DASHBOARD_MAINTENANCE_TASKS,
    query: { listingAlias },
  };
}

export function getTourAvailabilityRoute({ listingAlias }: { listingAlias: string }): {
  pathname: Route['pathname'];
  query: Route['query'];
} {
  return {
    pathname: ROUTES.TOUR_AVAILABILITY,
    query: { listingAlias },
  };
}

export function getTourCalendarRoute({ listingAlias, tourId }: { listingAlias: string; tourId?: string }): {
  pathname: Route['pathname'];
  query: Route['query'];
} {
  return {
    pathname: ROUTES.TOUR_CALENDAR,
    query: tourId ? { listingAlias, tourId } : { listingAlias },
  };
}

export function getPortfolioMembersRoute({ portfolioId }: { portfolioId: string }): {
  pathname: Route['pathname'];
  query: Route['query'];
} {
  return {
    pathname: ROUTES.PORTFOLIO_MEMBERS,
    query: { portfolioId },
  };
}

export function getPortfolioTeamsRoute({ portfolioId }: { portfolioId: string }): {
  pathname: Route['pathname'];
  query: Route['query'];
} {
  return {
    pathname: ROUTES.PORTFOLIO_TEAMS,
    query: { portfolioId },
  };
}

export function getPortfolioPropertiesRoute({ portfolioId }: { portfolioId: string }): {
  pathname: Route['pathname'];
  query: Route['query'];
} {
  return {
    pathname: ROUTES.PORTFOLIO_PROPERTIES,
    query: { portfolioId },
  };
}

export function getCostsAndFeesRoute({ listingAlias }: { listingAlias: string }): {
  pathname: Route['pathname'];
  query: Route['query'];
} {
  return {
    pathname: ROUTES.COSTS_AND_FEES,
    query: { listingAlias },
  };
}

export function getRentalProtectionManagePoliciesRoute({
  propertyId,
  moduleId,
}: {
  propertyId: string;
  moduleId?: string;
}): {
  pathname: Route['pathname'];
  query: Route['query'];
} {
  return {
    pathname: ROUTES.RENTAL_PROTECTION_MANAGE_POLICIES,
    query: moduleId ? { propertyId, moduleId: [moduleId] } : { propertyId },
  };
}
